/* eslint-disable react/react-in-jsx-scope */
import { isEmpty, extend } from 'underscore';
import { endsWith } from 'underscore.string';
import Promise from 'bluebird';
import React from 'react';
import moment from 'moment';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { languageParseMode } from './countdown-language';

import { CountdownEditor } from './countdown-editor';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';
import { parseRule, evaluateCountdownRules, parseCountdownRule } from './parser';
import { formatCountdown, defaultCountdownDays } from './utils';

const countDownDefaultDaysString = moment(new Date()).add(defaultCountdownDays, 'days').format('YYYY/MM/DD');
const RSACountDownDefaultDaysString = moment(new Date()).add(defaultCountdownDays, 'days').format('YYYY-MM-DD');

const pickerCountdownInsert = `{=countdown("${countDownDefaultDaysString}")}`;
const RSAPickerCountdownInsert = `{countdown(${RSACountDownDefaultDaysString})}`;

const isDatePicker = target =>
  target.closest('.ms-DatePicker-picker') !== null ||
  target.closest('.ms-DatePicker-dayPicker') !== null ||
  target.closest('.ms-DatePicker-monthPicker') !== null ||
  target.closest('.ms-DatePicker-yearPicker') !== null;

export class AdCustomizerCountdownAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({
    deps: {
      instrumentation,
      i18n,
      permissions,
      timeZone,
    },
    controller,
  }) {
    super({
      name: 'countdown',
      priority: 10,
      instrumentation,
      i18n,
      activityName: 'countdown',
      controller,
    });
    this.controller = controller;
    this.permissions = permissions;
    this.timeZone = timeZone;
  }

  getMenuItem({
    startIndex, source, target, updateAndCloseCallback, adType,
  }) {
    return {
      name: this.i18n.getString(_TL_('Countdown')),
      description: this.i18n.getString(_TL_('Insert a timer that counts down to an event')),
      onClick: (event) => {
        const endIndex = startIndex + source.length;

        const prefix = target.value.substring(0, startIndex);
        const suffix = target.value.substring(endIndex);
        let newValue;
        if (adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') {
          newValue = `${prefix}${RSAPickerCountdownInsert}${suffix}`;
        } else {
          newValue = `${prefix}${pickerCountdownInsert}${suffix}`;
        }
        this.defaultAnalyzeResult = parseRule({
          text: newValue,
          cursorIndex: newValue.length - suffix.length - 1,
          i18n: this.i18n,
          permissions: this.permissions,
          adType,
        });

        const cursorPosition = newValue.length - suffix.length - 1;

        updateAndCloseCallback({
          newValue,
          cursorPosition,
          event,
          isSelectedByUser: true,
        });

        this.controller.updatePicker({
          target,
          syntax: this.getSyntax(this.defaultAnalyzeResult, adType),
          editorView: this.getEditor({
            target,
            analyzeResult: this.defaultAnalyzeResult,
            updateAndCloseCallback,
            adType,
          }),
          container: this.controller.container,
          adType,
        });
      },
    };
  }

  isClickedAway(event) {
    return !isDatePicker(event.target);
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target, adType,
  }) {
    const processedText = text ? text.toLowerCase() : '';
    const rule = parseRule({
      text: processedText,
      cursorIndex,
      i18n: this.i18n,
      permissions: this.permissions,
      adType,
    });

    if (rule) {
      const { options: { startIndex, source } } = rule;
      return Promise.resolve(extend(
        {
          match: true,
          type: this.name,
          defaultHandlerForInput: false,
          itemsToDisplay: [this.getMenuItem({
            startIndex,
            source,
            updateAndCloseCallback,
            target,
            adType,
          })],
        },
        rule
      ));
    }

    // not match whole rule, see whether to show menu item
    let startIndex1 = -1;
    let source;
    if (endsWith(processedText, '{=', cursorIndex) && adType !== 'ResponsiveSearchAd' && adType !== 'ResponsiveAd') {
      startIndex1 = cursorIndex - 2;
      source = '{=';
    } else if (endsWith(processedText, '{', cursorIndex)) {
      startIndex1 = cursorIndex - 1;
      source = '{';
    }

    if (startIndex1 >= 0 && startIndex1 !== cursorIndex) {
      return Promise.resolve(extend({
        type: this.name,
        defaultHandlerForInput: false,
        itemsToDisplay: [this.getMenuItem({
          startIndex: startIndex1,
          source,
          updateAndCloseCallback,
          target,
          adType,
        })],
      }));
    }

    return Promise.resolve(null);
  }

  getSyntax(analyzeResult, adType) {
    const { options } = analyzeResult || {};
    const { type } = options || {};
    if (isEmpty(type)) {
      return null;
    }

    const syntax = formatCountdown(
      `${this.i18n.getString(_TL_('Syntax:'))} `,
      type,
      null,
      null,
      this.i18n.getString(_TL_('language')),
      this.i18n.getString(_TL_('daysBefore')),
      null,
      adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd'
    );
    return (
      <div className="countdownEditor-syntax">{syntax}
        <HelpPopupInline
          topic="pop_BA_NewAdCreate_Countdown_Syntax"
          ariaLabel={this.i18n.getString(_TL_('Click to learn more about {{item}}', { item: this.i18n.getString(_TL_('Syntax')) }))}
        />
      </div>
    );
  }

  // as long as the rule match (means has type), we can show the editor
  needEditor(analyzeResult) {
    const { options } = analyzeResult || {};
    const { type, includeAdCustomizerFeed } = options || {};
    return !includeAdCustomizerFeed && !isEmpty(type);
  }

  getEditor({
    target, analyzeResult, updateAndCloseCallback, adType,
  }) {
    const { options } = analyzeResult || {};

    return (
      <CountdownEditor
        i18n={this.i18n}
        options={options}
        updateAndCloseCallback={updateAndCloseCallback}
        target={target}
        adType={adType}
        permissions={this.permissions}
        timeZone={this.timeZone}
      />
    );
  }

  evaluateRule(text, adType) {
    return evaluateCountdownRules({
      text, i18n: this.i18n, permissions: this.permissions, adType,
    });
  }

  getCharCountExcludingAdCustomizers(text, adType) {
    let processedText = text;
    let charCount = 0;
    const rule = parseCountdownRule({
      text: processedText,
      startIndex: 0,
      languageParseMode: languageParseMode.ForCharacterCount,
      i18n: this.i18n,
      permissions: this.permissions,
      adType,
    });

    if (rule && rule.source) {
      if (rule.isComplete) {
        charCount += rule.language.maxLength;
      } else {
        charCount += rule.source.length;
      }
      processedText = processedText.replace(rule.source, '');
    }

    return {
      processedText,
      charCount,
    };
  }
}
